import { getBackendApiUrl } from "./appConfig.js";

const buildApiUrl = (path) => {
  const baseUrl = getBackendApiUrl();
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
};

export const apiUrl = {
  // Database routes
  dbHealth: () => buildApiUrl("/api/db/health"),
  checkDBConnection: () => buildApiUrl("/api/db/check-connection"),

  // User routes (/api/users prefix)
  users: () => buildApiUrl("/aipd/getUserList"),
  verifyUser: () => buildApiUrl("/api/users/verify"),

  // AIPD user routes (/aipd prefix)
  aipdGetUserList: () => buildApiUrl("/aipd/getUserList"),
  aipdGetUserDetails: () => buildApiUrl("/aipd/getUserDetails"),
  aipdUserListByEmail: () => buildApiUrl("/aipd/userListByEmail"),
  aipdUpdateUser: () => buildApiUrl("/aipd/updateUser"),
  aipdRegisterUser: () => buildApiUrl("/aipd/registerUser"),
  aipdDeleteUser: () => buildApiUrl("/aipd/deleteUser"),
  aipdGetUserGroupListForUser: () => buildApiUrl("/aipd/getUserGroupListForUser"),
  aipdUpdateDefaultUserGroup: () => buildApiUrl("/aipd/updateDefaultUserGroup"),
  aipdUpdateDefaultRlType: () => buildApiUrl("/aipd/updateDefaultRlType"),
  aipdNotifications: () => buildApiUrl("/aipd/message/notifications"),
  aipdNotificationStatus: () => buildApiUrl("/aipd/notification/status"),
  aipdReadAllNotifications: () => buildApiUrl("/aipd/notification/readAllNotifications"),
  aipdCreateCommentV2: () => buildApiUrl("/aipd/createComment/v2"),

  // AIPD overview & menu
  aipdGetOverview: () => buildApiUrl("/aipd/getOverview"),
  aipdGetReportByGroup: (groupId) => buildApiUrl(`/aipd/getreport?groupId=${groupId}`),
  aipdGetMenu: () => buildApiUrl("/aipd/getMenu"),
  aipdCompleteTracking: () => buildApiUrl("/aipd/completeTracking"),
  aipdReportsByGroup: (userGroupId) => buildApiUrl(`/aipd/userGroupId/reports?userGroupId=${userGroupId}`),
  aipdAnalytics: (params = {}) => {
    const q = new URLSearchParams();
    if (params.startDate) q.set("startDate", params.startDate);
    if (params.endDate)   q.set("endDate",   params.endDate);
    if (params.reportId)  q.set("reportId",  params.reportId);
    if (params.personaId) q.set("personaId", params.personaId);
    if (params.userEmail) q.set("userEmail", params.userEmail);
    const qs = q.toString();
    return buildApiUrl(`/aipd/analytics${qs ? `?${qs}` : ""}`);
  },
  aipdAnalyticsConfig: () => buildApiUrl("/aipd/analytics/config"),
  aipdUserReportListByGroup: (userGroupId) => buildApiUrl(`/aipd/userReportListByUserGroupId?userGroupId=${userGroupId}`),
  aipdTrackingByUser: (email) => buildApiUrl(`/aipd/trackingByUser?email=${email}`),
  aipdTrackingTimedByUser: (start, end, email) => buildApiUrl(`/aipd/trackingTimedByUser?start=${start}&end=${end}&email=${email}`),
  aipdViewsPerReportByPersonaWithTimeForEmail: (startDate, endDate, email) => buildApiUrl(`/aipd/viewsPerReportByPersonaWithTimeForEmail?startDate=${startDate}&endDate=${endDate}&email=${email}`),

  // AIPD reports & content
  aipdReportData: (reportId) => buildApiUrl(`/aipd/reports/${reportId}`),
  aipdReportExportPptx: (reportId) => buildApiUrl(`/aipd/reports/${reportId}/exportPptx`),
  aipdReportExportLivePptx: (reportId, pageName) => buildApiUrl(`/aipd/reports/${reportId}/exportLivePptx${pageName ? `?pageName=${encodeURIComponent(pageName)}` : ""}`),
  aipdReportExportImage: (reportId, options = {}) => {
    const params = new URLSearchParams();
    if (options.pageName) params.append("pageName", options.pageName);
    if (options.useDefaultValues !== undefined) params.append("useDefaultValues", options.useDefaultValues);
    if (options.excludeHiddenPages !== undefined) params.append("excludeHiddenPages", options.excludeHiddenPages);
    if (options.currentPageOnly !== undefined) params.append("currentPageOnly", options.currentPageOnly);
    const queryString = params.toString();
    return buildApiUrl(`/aipd/reports/${reportId}/exportImage${queryString ? `?${queryString}` : ""}`);
  },
  aipdReportPowerPointEmbedUrl: (reportId, pageName) => buildApiUrl(`/aipd/reports/${reportId}/powerPointEmbedUrl${pageName ? `?pageName=${encodeURIComponent(pageName)}` : ""}`),
  aipdGetFaq: (reportId, groupId) => buildApiUrl(`/aipd/getFaq/reportId/${reportId}${groupId ? `?groupId=${groupId}` : ""}`),
  aipdGetAllFaqs: () => buildApiUrl("/aipd/getFaq"),
  aipdPostFaq: () => buildApiUrl("/aipd/postFaq"),
  aipdUpdateFaq: () => buildApiUrl("/aipd/updateFaq"),
  aipdDeleteFaq: () => buildApiUrl("/aipd/deleteFaq"),
  aipdPosts: (topicIdentifier) => buildApiUrl(`/aipd/posts/v2/${topicIdentifier}`),
  aipdUserEmailsByUserGroupId: () => buildApiUrl("/aipd/userEmailsByUserGroupId"),
  aipdCreateTopicV2: () => buildApiUrl("/aipd/createTopic/v2"),
  aipdUpdateCommentV2: () => buildApiUrl("/aipd/updateComment/v2"),
  aipdDeleteCommentV2: () => buildApiUrl("/aipd/deleteComment/v2"),
  aipdGetBookmark: () => buildApiUrl("/aipd/getBookmark"),
  aipdGetBookmarks: () => buildApiUrl("/aipd/getBookmarks"),
  aipdSaveBookmarks: () => buildApiUrl("/aipd/saveBookmarks"),
  aipdDeleteBookmarks: () => buildApiUrl("/aipd/deleteBookmarks"),
  aipdUpdateBookmarkTitles: () => buildApiUrl("/aipd/updateBookmarkTitles"),
  aipdTracking: () => buildApiUrl("/aipd/tracking"),
  aipdBookmarkState: (bookmarkId) =>
    buildApiUrl(`/aipd/bookmarkState?bookmarkId=${bookmarkId}`),
  aipdReportPageFilterState: (reportId, pageName) =>
    buildApiUrl(
      `/aipd/reportPageFilterState${
        reportId ? `?reportId=${reportId}&pageName=${encodeURIComponent(pageName || "")}` : ""
      }`
    ),

  // AIPD custom visuals
  aipdCustomVisuals: (groupId) => buildApiUrl(`/aipd/customVisuals/getVisualsV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdCustomVisualsV2: (groupId) => buildApiUrl(`/aipd/customVisuals/getCustomVisualsV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdPersonaVisuals: (userGroupId) => buildApiUrl(userGroupId ? `/aipd/customVisuals/personaVisuals?userGroupId=${userGroupId}` : "/aipd/customVisuals/personaVisuals"),
  aipdSelectedVisuals: () => buildApiUrl("/aipd/customVisuals/SelectedVisuals"),
  aipdSelectedVisualsV2: () => buildApiUrl("/aipd/customVisuals/SelectedVisualsV2"),
  aipdSaveCustomVisuals: (groupId) => buildApiUrl(`/aipd/customVisuals/saveCustomVisualForUserV2${groupId ? `?groupId=${groupId}` : ""}`),
  aipdAddVisualsToCart: () => buildApiUrl("/aipd/customVisuals/addVisualsToCart"),
  aipdKpiFilterSelections: (groupId) =>
    buildApiUrl(`/aipd/customVisuals/filterSelections${groupId ? `?groupId=${groupId}` : ""}`),
  aipdSaveKpiStats: (groupId) => buildApiUrl(`/aipd/saveKpiStats${groupId ? `?groupId=${groupId}` : ""}`),
  // Legacy prompt-only path. Retained only for the ENGINE_ENABLED=false kill
  // switch during rollout; see docs/enterprise-engine/11-MIGRATION-PLAN.md.
  aipdChatReport: () => buildApiUrl("/aipd/chat/report"),
  aipdChatReportStream: () => buildApiUrl("/aipd/chat/report/stream"),
  // "Source Data" mode: answers from a live DAX query against the report's own
  // Power BI dataset (RLS-scoped), instead of the cached visual exports above.
  aipdChatReportSource: () => buildApiUrl("/aipd/chat/report/source"),
  aipdChatSessions: (reportId, groupId) =>
    buildApiUrl(
      `/aipd/chat/sessions?${reportId ? `reportId=${reportId}&` : ""}${groupId ? `groupId=${groupId}` : ""}`
    ),
  aipdChatSessionMessages: (sessionId) => buildApiUrl(`/aipd/chat/sessions/${sessionId}/messages`),

  // Enterprise Intelligence Engine — the reasoning path. The portal sends typed
  // report context and receives an evaluated answer with evidence; it never
  // sends a prompt and never composes an answer itself.
  aipdChatEngine: () => buildApiUrl("/aipd/chat/engine"),
  aipdChatEngineStream: () => buildApiUrl("/aipd/chat/engine/stream"),
  aipdChatEngineHealth: () => buildApiUrl("/aipd/chat/engine/health"),

  // Persona routes
  personas: () => buildApiUrl("/api/personas/list"),
  personaDisplayList: () => buildApiUrl("/api/personas/display-list"),
  personasWithUsers: () => buildApiUrl("/api/personas/with-users"),
  personaByName: (name) => buildApiUrl(`/api/personas/by-name/${name}`),
  personaUserGroups: () => buildApiUrl("/api/personas/user-groups"),
  createPersona: () => buildApiUrl("/api/personas/register"),
  updatePersona: (id) => buildApiUrl(`/api/personas/update/${id}`),
  deletePersona: (id) => buildApiUrl(`/api/personas/delete/${id}`),
  personaMapReports: () => buildApiUrl("/api/personas/map-reports"),
  personaMappedReports: (name) => buildApiUrl(`/api/personas/${name}/mapped-reports`),
  personaMappedReportsById: (id) => buildApiUrl(`/api/personas/${id}/mapped-reports`),

  // Report routes
  reports: () => buildApiUrl("/api/reports/list"),
  reportById: (id) => buildApiUrl(`/api/reports/${id}`),
  createReport: () => buildApiUrl("/api/reports/register"),
  updateReport: (id) => buildApiUrl(`/api/reports/${id}`),
  deleteReport: (id) => buildApiUrl(`/api/reports/${id}`),

  // Onboarding routes (text-to-DAX per-dashboard setup)
  onboardingStart: () => buildApiUrl("/api/onboarding/start"),
  onboardingState: (id) => buildApiUrl(`/api/onboarding/${id}`),
  onboardingIntrospect: (id) => buildApiUrl(`/api/onboarding/${id}/introspect`),
  onboardingTables: (id) => buildApiUrl(`/api/onboarding/${id}/tables`),
  onboardingGenDashboardQuestions: (id) => buildApiUrl(`/api/onboarding/${id}/generate/dashboard-questions`),
  onboardingGenSourceQuestions: (id) => buildApiUrl(`/api/onboarding/${id}/generate/source-questions`),
  onboardingGenKpis: (id) => buildApiUrl(`/api/onboarding/${id}/generate/kpis`),
  onboardingGenMetadata: (id) => buildApiUrl(`/api/onboarding/${id}/generate/metadata`),
  onboardingGenDomainContext: (id) => buildApiUrl(`/api/onboarding/${id}/generate/domain-context`),
  onboardingSaveDomainContext: (id) => buildApiUrl(`/api/onboarding/${id}/domain-context`),
  onboardingGenMeasureDax: (id) => buildApiUrl(`/api/onboarding/${id}/generate/measure-dax`),
  onboardingGenMeasuresDaxBulk: (id) => buildApiUrl(`/api/onboarding/${id}/generate/measures-dax-bulk`),
  onboardingGenQuestionDax: (id) => buildApiUrl(`/api/onboarding/${id}/generate/question-dax`),
  onboardingVerifyDax: (id) => buildApiUrl(`/api/onboarding/${id}/verify-dax`),
  onboardingVerifyMeasure: (id) => buildApiUrl(`/api/onboarding/${id}/verify-measure`),
  onboardingFeedback: (id) => buildApiUrl(`/api/onboarding/${id}/feedback`),
  onboardingFinalize: (id) => buildApiUrl(`/api/onboarding/${id}/finalize`),
  onboardingChat: () => buildApiUrl("/api/onboarding/chat"),
  onboardingChatStatus: (reportGuid) => buildApiUrl(`/api/onboarding/chat/status/${reportGuid}`),
  onboardingPrepopulate: (id) => buildApiUrl(`/api/onboarding/${id}/prepopulate`),
  onboardingDelete: (id) => buildApiUrl(`/api/onboarding/${id}`),
  onboardingReopen: (id) => buildApiUrl(`/api/onboarding/${id}/reopen`),
  onboardingStatusByGuid: (reportGuid) => buildApiUrl(`/api/onboarding/status/${reportGuid}`),

  // Role routes
  roles: () => buildApiUrl("/api/roles/list"),
  createRole: () => buildApiUrl("/api/roles/register"),
  updateRole: (id) => buildApiUrl(`/api/roles/${id}`),
  deleteRole: (id) => buildApiUrl(`/api/roles/${id}`),

  // Tool role routes (Page Management)
  toolRolePages: () => buildApiUrl("/api/tool-roles/pages"),
  toolRoles: () => buildApiUrl("/api/tool-roles/list"),
  createToolRole: () => buildApiUrl("/api/tool-roles/register"),
  updateToolRole: (id) => buildApiUrl(`/api/tool-roles/${id}`),
  deleteToolRole: (id) => buildApiUrl(`/api/tool-roles/${id}`),
  toolRoleMapPages: () => buildApiUrl("/api/tool-roles/map-pages"),
  toolRoleMappedPagesById: (id) => buildApiUrl(`/api/tool-roles/${id}/mapped-pages`),
  toolRoleUserPages: (email) => buildApiUrl(`/api/tool-roles/user-pages${email ? `?email=${email}` : ""}`),
};

export { buildApiUrl };
